import axiosClient from "./axiosClient";

/**
 * Transaction summary response
 */
export interface TransactionSummary {
  transaction_id: number;
  timestamp: string;
  user_id: number | null;
  user_type: string | null;
  models_changed: {
    [modelName: string]: {
      inserts: number;
      updates: number;
      deletes: number;
    };
  };
}

/**
 * Detailed transaction changes response
 */
export interface TransactionDetails {
  transaction_id: number;
  issued_at: string | null;
  user_id: number | null;
  user_type: string | null;
  changes: ChangeDetail[];
}

export interface ChangeDetail {
  model: string;
  operation: "insert" | "update" | "delete";
  id: number | null;
  changes?: {
    [fieldName: string]: {
      old: string | null;
      new: string | null;
    };
  };
  data?: {
    [fieldName: string]: string | null;
  };
}

/**
 * Record history entry
 */
export interface RecordHistoryEntry {
  transaction_id: number;
  timestamp: string | null;
  user_id: number | null;
  user_type: string | null;
  user_name: string | null;
  operation: "insert" | "update" | "delete";
  changes?: {
    [fieldName: string]: {
      old: string | null;
      new: string | null;
    };
  };
}

/**
 * Record history response
 */
export interface RecordHistoryResponse {
  model: string;
  record_id: number;
  history: RecordHistoryEntry[];
}

/**
 * User activity response
 */
export interface UserActivityResponse {
  user_id: number;
  period_days: number;
  total_transactions: number;
  models_affected: {
    [modelName: string]: {
      inserts: number;
      updates: number;
      deletes: number;
    };
  };
  operation_counts: {
    inserts: number;
    updates: number;
    deletes: number;
  };
  transactions: Array<{
    transaction_id: number;
    timestamp: string;
  }>;
}

/**
 * Audit statistics response
 */
export interface AuditStatsResponse {
  period_days: number;
  total_transactions: number;
  unique_users: number;
  models_modified: string[];
  operation_counts: {
    inserts: number;
    updates: number;
    deletes: number;
  };
  start_date: string;
}

/**
 * Get summary or detailed changes for a specific transaction
 */
export async function getTransaction(
  transactionId: number,
  detailed: boolean = false
): Promise<TransactionSummary | TransactionDetails> {
  try {
    const response = await axiosClient.get(
      `/audit/transaction/${transactionId}`,
      {
        params: { detailed: detailed ? "true" : "false" },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch transaction:", error);
    throw error;
  }
}

/**
 * Get recent transaction changes with optional filtering
 */
export async function getRecentChanges(params?: {
  limit?: number;
  user_id?: number;
  user_type?: string;
  model?: string;
}): Promise<TransactionSummary[]> {
  try {
    const response = await axiosClient.get("/audit/recent", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch recent changes:", error);
    throw error;
  }
}

/**
 * Get changes within a specific date range
 */
export async function getChangesByDateRange(
  startDate: string,
  endDate: string,
  model?: string
): Promise<TransactionSummary[]> {
  try {
    const params: any = {
      start_date: startDate,
      end_date: endDate,
    };
    if (model) {
      params.model = model;
    }
    const response = await axiosClient.get("/audit/date-range", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch changes by date range:", error);
    throw error;
  }
}

/**
 * Get complete change history for a specific record
 */
export async function getRecordHistory(
  modelName: string,
  recordId: number,
  limit: number = 20
): Promise<RecordHistoryResponse> {
  try {
    const response = await axiosClient.get(
      `/audit/model/${modelName}/${recordId}`,
      {
        params: { limit },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch record history:", error);
    throw error;
  }
}

/**
 * Get activity summary for a specific user
 */
export async function getUserActivity(
  userId: number,
  days: number = 7,
  userType?: string
): Promise<UserActivityResponse> {
  try {
    const params: any = { days };
    if (userType) {
      params.user_type = userType;
    }
    const response = await axiosClient.get(`/audit/user/${userId}/activity`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch user activity:", error);
    throw error;
  }
}

/**
 * Get activity summary for the currently authenticated user
 */
export async function getMyActivity(
  days: number = 7,
  userType?: string
): Promise<UserActivityResponse> {
  try {
    const params: any = { days };
    if (userType) {
      params.user_type = userType;
    }
    const response = await axiosClient.get("/audit/my-activity", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch my activity:", error);
    throw error;
  }
}

/**
 * Get overall audit statistics
 */
export async function getAuditStats(
  days: number = 30
): Promise<AuditStatsResponse> {
  try {
    const response = await axiosClient.get("/audit/stats", {
      params: { days },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch audit stats:", error);
    throw error;
  }
}
